import React from 'react';

import styled from 'styled-components';

import { Button } from '../Button';
import { Icon } from '../Icon';
import { LESSON_TYPE_CONFIG, type LessonItemProps } from './types';

const CaptionSeparator = styled.span`
  margin: 0 4px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const CaptionText = styled.span`
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StyledLessonItem = styled.div`
  display: flex;
  align-items: flex-start;
  padding: var(--space-250, 20px) var(--space-200, 16px);
  border-top: 1px solid var(--stile-grey-2);
  gap: var(--space-200, 16px);

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const LessonImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: var(--space-200, 16px);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: var(--space-100, 8px);
    width: 82px;
  }
`;

const LessonImage = styled.div`
  flex-shrink: 0;
  width: 80px;
  height: 56px;
  border-radius: 8px;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 82px;
    height: 57px;
  }

  img,
  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const LessonInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  @media (max-width: 768px) {
    width: 100%;
    order: 1;
  }
`;

const LessonTitle = styled.h6`
  margin: 0;
  color: var(--stile-grey-8);

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const LessonMeta = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-100, 8px);
  font-size: 14px;
  color: var(--stile-grey-6);

  @media (max-width: 768px) {
    font-size: 12px;
    flex-wrap: wrap;
  }
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: var(--space-050, 4px);

  svg {
    width: 20px;
    height: 20px;
  }

  img {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const DesktopButtonWrapper = styled.div`
  flex-shrink: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileButtonWrapper = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;

    .button {
      display: flex;
      width: 100%;
      justify-content: center;

      &.--secondary {
        padding: 9px 0;
      }
    }
  }
`;

export default function LessonItem({
  title,
  image,
  lessonType,
  caption,
  previewLink,
  iconSrc,
}: LessonItemProps) {
  const lessonTypeConfig = LESSON_TYPE_CONFIG[lessonType];

  return (
    <StyledLessonItem>
      <LessonImageWrapper>
        <LessonImage>{image}</LessonImage>
        {previewLink && (
          <MobileButtonWrapper>
            <Button
              style="secondary"
              size="small"
              href={previewLink}
              label="Preview"
              newTab={true}
            />
          </MobileButtonWrapper>
        )}
      </LessonImageWrapper>
      <LessonInfo>
        <LessonTitle className="h7">{title}</LessonTitle>
        <LessonMeta>
          <MetaItem>
            <img src={iconSrc || lessonTypeConfig.iconSrc} alt="" />
            {lessonTypeConfig.name}
          </MetaItem>
          {caption && (
            <>
              <CaptionSeparator>·</CaptionSeparator>
              <CaptionText>{caption}</CaptionText>
            </>
          )}
        </LessonMeta>
      </LessonInfo>
      {previewLink && (
        <DesktopButtonWrapper>
          <Button
            style="secondary"
            href={previewLink}
            label={
              <div>
                <Icon
                  iconId="natural-user-interface"
                  iconSize="fontSize"
                  style={{ marginRight: 10 }}
                />
                Preview
              </div>
            }
            newTab={true}
          />
        </DesktopButtonWrapper>
      )}
    </StyledLessonItem>
  );
}
